/**
 * ПОЛОСА ВИЗУАЛА — картинка поста и решения по ней, ПОД превью и по его ширине.
 *
 * Требование Ильи 06.09.2026 дословно: картинка показывается под постом ровно так же, как
 * показан сам пост, а рядом — принять, удалить, перегенерировать с комментарием, загрузить
 * свою; для постов, которые сервис решил не рисовать, — нарисовать. Описание при этом с
 * правки 10 (§8.1) НЕОБЯЗАТЕЛЬНО: пустое поле означает «возьми факты из поста» и уезжает
 * литералом `VISUAL_AUTO_PROMPT` (см. шапку `GenerateVisualDialog`).
 *
 * ПУСТОГО СОСТОЯНИЯ У ПОЛОСЫ НЕТ НИ ОДНОГО. Она рисуется всегда, когда открыт черновик, и
 * «картинки нет» занимает тот же прямоугольник, ту же шапку и тот же ряд кнопок, что
 * «картинка есть». Это и есть ответ на «как сделать отказ видимым»: отсутствие, которое
 * занимает столько же места, сколько присутствие, перестаёт быть отсутствием. Причина у
 * «нет» обязательна — полоса без причины хуже отсутствия полосы: без неё «нет» читается как
 * сбой, и человек жмёт «нарисовать» из подозрения, а не из замысла.
 *
 * «ОТКЛОНИТЬ» И «УДАЛИТЬ КАРТИНКУ» — РАЗНЫЕ СЛОВА ПОТОМУ, ЧТО ЭТО РАЗНЫЕ МЕХАНИЗМЫ.
 * Отклонение (`POST …/visual/reject`) версии не создаёт вовсе: предложения нет в подписываемом
 * содержании, и `content_hash` оно не трогает. Удаление (`POST …/versions` с `media: null`)
 * создаёт v+1 и меняет хэш. Одно слово на два механизма врало бы про последствие.
 *
 * ОТКЛОНЕНИЕ НЕОБРАТИМО, и кнопки «вернуть картинку» здесь нет. Не по вкусу: переход
 * состояния предложения из разобранного запрещён триггером `visual_proposal_state_final`
 * (`migrations/0015_visuals.sql`), то есть кнопка «вернуть» была бы обещанием, которое база
 * не исполнит. Передумавший человек жмёт «Нарисовать картинку…».
 *
 * ОБЛОЖКА (docs/phase-2/10-posters.md) — картинка ВЕРСИИ, а не предложение: она приезжает
 * внутри поста, и утверждение поста утверждает её. Полоса показывает, что на ней и почему —
 * формат крючка, фон, прочтение поста, день в углу, — и даёт два хода: убрать (v+1 без медиа,
 * как любую картинку) и перерисовать с комментарием (`poster:` переделкой). Инфографика у
 * бренда с обложкой остаётся доступной отдельной кнопкой и приезжает предложением, как всегда.
 */

import { DRAFT_TRANSITIONS, type DraftState } from '@smm/contracts';
import { getFirstUrl } from '@smm/contracts/preview';
import { MECHANISM_REASON_PREFIX, posterDateLabel } from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import { agentsOffReason, offLabel, useBrands } from '../../brand.tsx';
import { IconExternal } from '../../components/icons.tsx';
import type { VisualStripProps } from '../contracts.ts';
import { lockReason, PICTURE_OBJECT } from '../versionLock.ts';
import { saveErrorText, saveVersion } from '../versionSave.ts';
import { GenerateVisualDialog } from './GenerateVisualDialog.tsx';
import styles from './VisualStrip.module.css';
import {
  POSTER_FORMAT_LABEL,
  POSTER_MOOD_LABEL,
  posterReasonText,
  visualHeadline,
  visualIssues,
  visualViewOf,
} from './visualState.ts';

/**
 * Состояния, из которых версия может быть вытеснена новой. Набор ВЫВОДИТСЯ из рёбер
 * контракта, а не переписывается списком: он обязан совпадать с `SUPERSEDABLE` бэкенда
 * (`domain/intake.ts`), и второй список разошёлся бы с ним молча. Ровно так же он выведен
 * в редакторе исходника.
 */
const EDITABLE: ReadonlySet<DraftState> = new Set(
  DRAFT_TRANSITIONS.filter((t) => t.to === 'superseded').map((t) => t.from),
);

/** Ответ обоих решений по картинке (`domain/visual.ts`, `VisualDecisionResult`). */
interface VisualDecisionResponse {
  readonly state: 'accepted' | 'rejected';
  readonly version_created: boolean;
  readonly version_no: number;
}

const reasonOf = (e: ApiRequestError): string | null => {
  const d = e.details;
  if (typeof d !== 'object' || d === null) return null;
  const r = (d as Record<string, unknown>)['reason'];
  return typeof r === 'string' ? r : null;
};

/**
 * Отказы решения по картинке — словами, а не кодами. Три из четырёх причин человек устраняет
 * сам, и «не удалось» вместо причины превращает устранимое в необъяснимое.
 */
function decisionErrorText(e: unknown, versionNo: number): string {
  if (!(e instanceof ApiRequestError)) return saveErrorText(e);
  const reason = reasonOf(e);
  if (reason === 'stale_base_version') {
    return (
      `Пока вы решали, появилась версия новее v${String(versionNo)}: её сохранил кто-то другой ` +
      'или соседняя вкладка. Экран перезаполнен — решите по картинке заново.'
    );
  }
  if (reason === 'no_pending_visual') {
    return 'Предложение уже разобрано — возможно, в соседней вкладке. Экран обновлён.';
  }
  return saveErrorText(e);
}

export function VisualStrip({
  model,
  upload,
  showProposal,
  onShowProposal,
  onChanged,
}: VisualStripProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Открытый диалог «нарисовать»: инфографика по фактам или обложка. */
  const [dialog, setDialog] = useState<'visual' | 'poster' | null>(null);

  const view = visualViewOf(model);
  const editable = EDITABLE.has(model.status);
  // Картинку рисует сервис генерации — переделкой с `targets: ['visual']`. У бренда без него
  // «Нарисовать…» и «Перерисовать…» гаснут с причиной (docs/multi-brand.md §8); загрузить
  // свою картинку можно по-прежнему — это делает человек.
  const drawOff = agentsOffReason(useBrands(), model.brand, ['generation']);
  const canonical = model.canonical;

  const run = (work: Promise<string>): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    work
      .then((text) => {
        setNotice(text);
        onChanged();
      })
      .catch((e: unknown) => {
        setError(decisionErrorText(e, model.version_no));
        // Расхождение версий и разобранное предложение — не отказ действия, а устаревший
        // экран: перечитать его надо в обоих случаях, иначе следующая кнопка отобьётся так же.
        if (e instanceof ApiRequestError && e.status === 409) onChanged();
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const decide = (decision: 'accept' | 'reject'): void => {
    run(
      post<VisualDecisionResponse>(
        `/api/v1/drafts/${model.draft_id}/versions/${String(model.version_no)}/visual/${decision}`,
      ).then((res) =>
        res.state === 'rejected'
          ? 'Предложение отклонено. Пост уйдёт без картинки, карточка первоисточника останется.'
          : res.version_created
            ? `Картинка принята и сохранена как v${String(res.version_no)}. Пост уйдёт с ней, первым блоком.`
            : 'Предложение разобрано: картинка в версии уже стояла, новой версии не появилось.',
      ),
    );
  };

  const removeMedia = (): void => {
    run(
      // Текст и сущности — из сохранённой версии побайтово: снятие картинки не имеет права
      // поменять ни одного знака поста. `link_preview_options` не отправляется — карточка
      // вернётся сама, по первой ссылке текста.
      saveVersion({
        draftId: model.draft_id,
        text: canonical.text,
        entities: canonical.entities,
        media: null,
        baseVersionNo: model.version_no,
      }).then(
        (res) =>
          `Картинка убрана, сохранено как v${String(res.version_no)}: пост уйдёт текстом, а карточка ` +
          'превью ссылки вернётся, если в тексте есть ссылка.',
      ),
    );
  };

  /* ── тело: что сейчас с картинкой и что будет ────────────────────────────── */

  const card = getFirstUrl(canonical);

  const bodyText = (): JSX.Element => {
    switch (view.state) {
      case 'proposed':
        return (
          <>
            <span className="small">
              Картинка заменяет собой строку поста: «{view.insteadOfText}»
            </span>
            <span className="micro">Почему нарисовали: {view.reason}</span>
            <span className="small">
              {card.kind === 'card'
                ? `Примете — картинка встанет первым блоком, пост уйдёт расширенным сообщением, и карточки превью ссылки не будет: сейчас её дал бы ${card.host}.`
                : 'Примете — картинка встанет первым блоком. Карточки превью ссылки у этого поста нет и сейчас.'}
            </span>
          </>
        );

      case 'attached':
        if (view.poster !== null) {
          const p = view.poster;
          return (
            <>
              <span className="small">
                {`Обложка в версии v${String(model.version_no)}: крючок «${p.hook}», формат «${POSTER_FORMAT_LABEL[p.hook_format]}», фон ${p.background}. Она входит в подписываемое содержание и уйдёт в канал первым блоком.`}
              </span>
              <span className="micro">
                {`Прочтение поста: ${POSTER_MOOD_LABEL[p.read.mood]} · важность ${String(p.read.importance)} · шок ${String(p.read.shock)} · юмор ${String(p.read.humor)} · серьёзность ${String(p.read.seriousness)} · практичность ${String(p.read.practical)}. ${p.read.summary}`}
              </span>
              {p.why === '' ? null : <span className="micro">{`Почему так: ${p.why}`}</span>}
              <span className="micro">
                {`В углу — ${posterDateLabel(p.date)}: утвердить пост можно только в слот этого дня. Слот другого дня — диалог утверждения предложит перерисовать дату.`}
              </span>
            </>
          );
        }
        return (
          <>
            <span className="small">
              Картинка в версии v{model.version_no}: она входит в подписываемое содержание и уйдёт в
              канал первым блоком.
            </span>
            <span className="micro">
              Карточки превью ссылки у поста с картинкой не бывает: параметра карточки у
              расширенного сообщения нет вовсе.
            </span>
            {view.reason === '' ? null : (
              <span className="micro">Вердикт сервиса: {posterReasonText(view.reason)}</span>
            )}
          </>
        );

      case 'none': {
        // «Не нарисовали» бывает ДВУХ РАЗНЫХ ВИДОВ, и путать их нельзя. Здоровый отказ —
        // редакционное решение: рисовать нечего. Сломанный механизм — вердикт не состоялся
        // вовсе, и тогда пост остался без картинки НЕ потому, что её там не место.
        //
        // Пока разницы не было видно, экран подписывал «это штатный исход, а не сбой» под
        // текстом четырёхсотки от API — то есть уверял человека, что сломанное работает.
        // Ровно так вырождение и прячется: механизм мёртв, а доля «не рисуем» выглядит
        // здоровой на всех постах сразу.
        const broken = view.reason.startsWith(MECHANISM_REASON_PREFIX);
        if (view.poster) {
          return (
            <>
              <span className="small">Причина: {posterReasonText(view.reason)}</span>
              <span className="micro">
                Обложку не нарисовали, и пост уйдёт без неё. Утверждать можно прямо сейчас, а можно
                нарисовать обложку заново: пост уйдёт на переделку и вернётся с ней.
              </span>
            </>
          );
        }
        return (
          <>
            <span className="small">Причина: {view.reason}</span>
            {broken ? (
              <span className="micro warn">
                ЭТО СБОЙ, а не решение: вердикт не состоялся, и текст поста тут ни при чём. Пост
                можно утвердить и без картинки, но саму причину надо чинить — пока она держится,
                картинки не будет ни у одного поста. Если сбой уже починили, перегенерируйте пост:
                причина записывается один раз, в момент генерации, и сама не обновляется.
              </span>
            ) : (
              <span className="micro">
                Это штатный исход, а не сбой: картинка ставится только там, где в тексте есть
                сравнение, порядок шагов или цифра с базой сравнения. Утверждать пост можно прямо
                сейчас.
              </span>
            )}
          </>
        );
      }

      case 'undrawn':
        if (view.poster) {
          return (
            <span className="small">
              Обложку убрали из этой версии правкой, и пост уйдёт без картинки. Вернуть обложку —
              «Нарисовать обложку…»: пост уйдёт на переделку и вернётся с новой.
            </span>
          );
        }
        return (
          <>
            <span className="small">
              Сервис решил рисовать, но картинки в версии нет: предложение либо отклонено вами, либо
              не дошло до версии. Пост уйдёт без картинки, карточка первоисточника останется.
            </span>
            {view.reason === '' ? null : (
              <span className="micro">Вердикт сервиса был: {view.reason}</span>
            )}
          </>
        );

      case 'unset':
        return (
          <span className="small">
            Вердикта по визуалу у этой версии нет: она создана до того, как механизм появился.
            Утверждению это не мешает.
          </span>
        );
    }
  };

  /* ── миниатюра ───────────────────────────────────────────────────────────── */

  const thumbId =
    view.state === 'proposed'
      ? view.mediaId
      : view.state === 'attached'
        ? (model.media_ref?.media_id ?? null)
        : null;

  const thumb = (): JSX.Element | null => {
    if (thumbId === null) return null;
    const ref = view.state === 'attached' ? model.media_ref : null;
    const w = ref?.width ?? null;
    const h = ref?.height ?? null;
    return (
      <div className={styles['thumbWrap']}>
        {/* alt пуст по той же причине, что и в превью: выдуманное описание соврало бы про
            то, что увидит подписчик. Смысл картинки несёт строка вердикта рядом. */}
        <img
          className={styles['thumb']}
          src={`/api/v1/media/${thumbId}`}
          alt=""
          style={
            w !== null && h !== null ? { aspectRatio: `${String(w)} / ${String(h)}` } : undefined
          }
        />
      </div>
    );
  };

  /* ── кнопки ──────────────────────────────────────────────────────────────── */

  const ghost = (label: string, onClick: () => void): JSX.Element => (
    <button
      type="button"
      className={busy || upload.busy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'}
      disabled={busy || upload.busy}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const uploadButton = (label: string): JSX.Element => ghost(label, upload.pick);

  // Погашенная — `aria-disabled` с причиной в имени и подсказке, как кнопки шапки и
  // «Переделать…»: выключенная кнопка не получает ни фокуса, ни наведения.
  const redrawButton = (label: string, kind: 'visual' | 'poster' = 'visual'): JSX.Element =>
    drawOff === null ? (
      ghost(label, () => {
        setDialog(kind);
      })
    ) : (
      <button
        type="button"
        className="btn btn-sm btn-dis"
        aria-disabled="true"
        aria-label={offLabel(label.replace(/…$/, ''), drawOff)}
        title={offLabel(label.replace(/…$/, ''), drawOff)}
      >
        {label}
      </button>
    );

  const actions = (): JSX.Element => {
    /*
     * ОТКЛОНЕНИЕ ДОСТУПНО ТАМ, ГДЕ ПРИНЯТИЕ УЖЕ НЕТ, и это не послабление, а единственный
     * выход. Принятие рождает v+1 и требует состояния из `SUPERSEDABLE`; отклонение
     * содержания не трогает вовсе и разрешено бэкендом всегда (`domain/visual.ts`,
     * `rejectVisual` статус не проверяет). Уберите здесь «Отклонить» — и пост в состоянии
     * `failed` с висящим предложением нельзя будет ни утвердить (гейт), ни разблокировать.
     */
    if (view.state === 'proposed') {
      return (
        <>
          {editable ? (
            <button
              type="button"
              className={busy || upload.busy ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
              disabled={busy || upload.busy}
              onClick={() => {
                decide('accept');
              }}
            >
              Принять картинку
            </button>
          ) : null}
          {ghost('Отклонить', () => {
            decide('reject');
          })}
          {editable ? redrawButton('Перерисовать картинку…') : null}
          {editable ? uploadButton('Загрузить свою…') : null}
          {editable ? null : (
            <span className="micro">
              {`Принять картинку нельзя: ${lockReason(model.status, PICTURE_OBJECT)}. Отклонить — можно, и это снимет блокировку.`}
            </span>
          )}
        </>
      );
    }
    if (!editable) {
      return (
        <span className="micro">
          {`Картинку этой версии не поменять: ${lockReason(model.status, PICTURE_OBJECT)}.`}
        </span>
      );
    }
    switch (view.state) {
      case 'attached':
        if (view.poster !== null) {
          return (
            <>
              {ghost('Убрать обложку', removeMedia)}
              {redrawButton('Перерисовать обложку…', 'poster')}
              {redrawButton('Инфографика вместо обложки…')}
              {uploadButton('Загрузить другую…')}
            </>
          );
        }
        return (
          <>
            {ghost('Удалить картинку', removeMedia)}
            {view.verdict === null ? null : redrawButton('Перерисовать картинку…')}
            {uploadButton('Загрузить другую…')}
          </>
        );
      case 'none':
      case 'undrawn':
        if (view.poster) {
          return (
            <>
              {redrawButton('Нарисовать обложку…', 'poster')}
              {redrawButton('Нарисовать инфографику…')}
              {uploadButton('Загрузить свою…')}
            </>
          );
        }
        return (
          <>
            {redrawButton('Нарисовать картинку…')}
            {uploadButton('Загрузить свою…')}
          </>
        );
      case 'unset':
        return uploadButton('Загрузить свою…');
    }
  };

  /**
   * Полоса красится и объявляет гейт ровно тогда, когда гейт ДЕЙСТВИТЕЛЬНО закрывает кнопку
   * утверждения, — то есть по тому же значению, что выключает её в полосе решений. Второй
   * критерий («состояние = предложено») сказал бы «утвердить нельзя» там, где утверждения и
   * так нет, — например на запланированном посте.
   */
  const gateOpen = visualIssues(model).length > 0;

  return (
    <section
      className={`${styles['strip']} ${gateOpen ? styles['stripGate'] : ''}`}
      aria-label="Визуал поста"
      data-testid="visual-strip"
    >
      <div className={styles['head']}>
        <span className="eyebrow">Визуал</span>
        <span className="meta">{visualHeadline(view)}</span>
      </div>

      {upload.dragging ? (
        <div className={styles['dropHint']}>Отпустите файл — он станет картинкой этого поста</div>
      ) : null}

      <div className={styles['body']}>
        {thumb()}
        <div className={styles['text']}>{bodyText()}</div>
      </div>

      {gateOpen ? (
        <div className="val val-err">
          <span>
            Пока по картинке нет решения, утвердить пост нельзя. Примите её или отклоните — оба
            ответа равноценны.
          </span>
        </div>
      ) : null}

      <div className={styles['actions']}>
        {actions()}
        {view.state === 'proposed' ? (
          <button
            type="button"
            className={`seg-o${showProposal ? ' on' : ''}`}
            aria-pressed={showProposal}
            onClick={() => {
              onShowProposal(!showProposal);
            }}
          >
            {showProposal ? 'Убрать из превью' : 'Показать в посте'}
          </button>
        ) : null}
        {thumbId === null ? null : (
          <a
            className="btn btn-ghost btn-sm"
            href={`/api/v1/media/${thumbId}`}
            target="_blank"
            rel="noreferrer noopener"
            style={{ textDecoration: 'none' }}
          >
            <IconExternal /> Открыть в полный размер
          </a>
        )}
      </div>

      {/* Клавиатурный путь загрузки. У зоны сброса роли ARIA нет вовсе, поэтому кнопка
          «Загрузить свою…» обязана быть настоящей кнопкой, кликающей по этому полю. */}
      <input
        ref={upload.inputRef}
        type="file"
        accept={upload.accept}
        onChange={upload.onInputChange}
        style={{ display: 'none' }}
        data-testid="visual-file-input"
      />

      {editable ? (
        <span className="micro">Файл можно перетащить прямо на пост. PNG или JPEG, до 5 МБ.</span>
      ) : null}

      {upload.stage === null ? null : <span className="micro">{upload.stage}</span>}
      {busy ? <span className="micro">Отправляем решение по картинке…</span> : null}

      {/* Отказ загрузки и отказ решения показываются РЯДОМ, а не вместо друг друга: они
          приходят от разных действий, и вытеснение одного другим прятало бы причину. */}
      {upload.error === null ? null : (
        <div className="val val-err">
          <span>{upload.error}</span>
        </div>
      )}
      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}
      {upload.notice === null ? null : (
        <div className="val val-info">
          <span>{upload.notice}</span>
        </div>
      )}
      {notice === null ? null : (
        <div className="val val-info">
          <span>{notice}</span>
        </div>
      )}

      {dialog === null ? null : (
        <GenerateVisualDialog
          model={model}
          kind={dialog}
          redraw={
            dialog === 'poster'
              ? view.state === 'attached' && view.poster !== null
              : view.state === 'proposed' || (view.state === 'attached' && view.poster === null)
          }
          onChanged={onChanged}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </section>
  );
}
